import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from '../../../utils/axiosConfig';
import '../../../Pages/Css/modalPermisosRoles.css';
import ModalBaseSm from '../../modals/modal-base-sm';
import FormModalCreatePermission from '../forms/formModalCreatePermission';

const READ_ENDPOINT = `${process.env.REACT_APP_SERVER}/usuarios/getpaginas`;
const CREATE_ENDPOINT = `${process.env.REACT_APP_SERVER}/usuarios/paginas`; // cámbiala si aplica

const ModalPermissions = () => {
    const [perms, setPerms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    // ---- MODAL CREAR ----
    const [showCreate, setShowCreate] = useState(false);
    const [path, setPath] = useState('');
    const [open, setOpen] = useState(false);
    const [create, setCreate] = useState(false);
    const [edit, setEdit] = useState(false);
    const [remove, setRemove] = useState(false);
    const [infoPermission, setInfoPermission] = useState(null);
    const canSubmit = useMemo(() => path.trim().length > 0, [path]);

    const [idEditPermission, setIdEditPermission] = useState(-1);

    const resetForm = useCallback(() => {
        setPath('');
        setOpen(false);
        setCreate(false);
        setEdit(false);
        setRemove(false);
    }, []);

    const authCfg = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('tkn')}`
        },
        withCredentials: true
    };

    const normalizeRow = (r, idx) => ({
        // clave solo para React (estable aunque no haya id)
        key: r.IdPagina ?? r.id ?? `tmp-${idx + 1}`,

        // ESTE es el id para actualizar en backend
        backendId: r.IdPagina ?? r.id,

        path: r.DescripcionPagina ?? r.url ?? r.path ?? "",
        open: Boolean(r.open ?? r.Abrir ?? r.bitOpen ?? false),
        create: Boolean(r.create ?? r.Crear ?? r.bitInsert ?? false),
        edit: Boolean(r.edit ?? r.Editar ?? r.bitEdit ?? false),
        remove: Boolean(r.remove ?? r.Eliminar ?? r.bitDelete ?? false),
        Notas: r.Notas ?? r.notes ?? "",
    });


    const getPermissions = async () => {
        setLoading(true);
        setErr(null);
        try {
            const res = await axios.post(READ_ENDPOINT, {}, authCfg);
            if (res?.data?.success === false) {
                window.location = "/";
                return;
            }
            const raw = Array.isArray(res?.data?.data) ? res.data.data : [];
            console.log("Permisos obtenidos: ", raw);
            setPerms(raw.map(normalizeRow));
        } catch (e) {
            console.error("Error al obtener permisos: ", e);
            setErr("No se pudieron cargar los permisos.");
        } finally {
            setLoading(false);
        }
    };

    const createPermission = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        const payload = {
            // genéricos
            path, open, create, edit, remove,
            // legacy / alternativos
            DescripcionPagina: path,
            Abrir: open, Crear: create, Editar: edit, Eliminar: remove,
            bitOpen: open, bitInsert: create, bitEdit: edit, bitDelete: remove,
        };

        try {
            const res = await axios.post(CREATE_ENDPOINT, payload, authCfg);
            const saved = res?.data?.data ?? payload;
            setPerms(prev => [normalizeRow(saved, prev.length), ...prev]);
            resetForm();
            setShowCreate(false);
        } catch (e) {
            console.error("Error al crear permiso: ", e);
            alert("No se pudo crear el permiso.");
        }
    };
    const update = (p) => {
        setInfoPermission(p);
        setIdEditPermission(p.backendId);
        setShowCreate(true);
        console.log("Editar permiso: ", p);
    }

    // cerrar modal con ESC o clic fuera
    useEffect(() => {
        const onKey = (ev) => ev.key === 'Escape' && setShowCreate(false);
        if (showCreate) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showCreate]);

    useEffect(() => {
        getPermissions();
    }, []);

    return (
        <div className="p-4">
            {loading && <p>Cargando…</p>}
            {err && <p className="text-red-600">{err}</p>}

            {/* Tabla */}
            <div
                style={{ height: '300px', overflowY: 'auto', overflowX: 'auto' }}
                className="bg-white border rounded-2xl shadow-md mb-4"
            >
                <table className="min-w-full text-sm table-auto">
                    <thead className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur border-b shadow-sm">
                        <tr className="text-gray-700">
                            <th className="text-left px-4 py-3">Página / Path</th>
                            <th className="text-left px-4 py-3">Notas</th>
                            <th className="text-center px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {perms.length === 0 ? (
                            <tr>
                                <td className="px-4 py-4 text-gray-500" colSpan={5}>Sin permisos.</td>
                            </tr>
                        ) : (
                            perms.map((p, i) => (
                                <tr
                                    key={p.id}
                                    className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}
                                >
                                    <td className="px-4 py-3 text-gray-900">{p.path}</td>
                                    <td className="px-4 py-3 text-gray-900">{p.Notas}</td>

                                    <td className="px-4 py-3 text-center">
                                        <button
                                            type="button"
                                            className="btn--edit"
                                            onClick={() => update(p)}
                                            aria-label="Editar permiso"
                                        >
                                            <i className="bx bxs-pencil icon"></i>
                                            <span>Editar</span>
                                        </button>
                                    </td>

                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Botón para abrir modal */}
            <div className="flex justify-end mb-2">
                <button
                    className="btn-primary-modals-permisos-roles"
                    onClick={() => setShowCreate(true)}
                >
                    Nueva página
                </button>
            </div>

            {/* Modal Crear Permiso */}
            <ModalBaseSm
                isOpen={showCreate}
                title={idEditPermission !== -1 ? 'Editar Página' : 'Crear Página'}
                onClose={() => { setShowCreate(false); getPermissions(); setIdEditPermission(-1); setInfoPermission(null); }}
                childComponent={<FormModalCreatePermission idEditPermission={idEditPermission} data={infoPermission} onClose={() => { setShowCreate(false); getPermissions(); setIdEditPermission(-1); setInfoPermission(null); }} />}
            />

        </div>
    );
};

export default ModalPermissions;
